/** API versioning */
export const API_VERSION = 'v1' as const;
export const API_PREFIX = `/api/${API_VERSION}` as const;

/** HTTP headers used between the app and the gateway. */
export const HEADERS = {
  requestId: 'x-request-id',
  authorization: 'authorization',
  appVersion: 'x-app-version',
  appPlatform: 'x-app-platform',
  acceptLanguage: 'accept-language',
  /** Stable per-install id the app sends with every request (usage stats, device last-seen). */
  installationId: 'x-installation-id',
  /** Required on the browser cookie routes (`routes.auth.web`), with the value {@link REQUESTED_WITH_FETCH}. */
  requestedWith: 'x-requested-with',
  /** Conditional draft save: the version the client last saw (see `parseDraftVersion`). */
  ifMatch: 'if-match',
  /** The draft's version after a save, as an entity-tag (see `formatDraftVersion`). */
  etag: 'etag',
} as const;

/**
 * Value of `X-Requested-With` on the browser cookie routes. A cross-site form cannot
 * set the header, and a cross-site `fetch` with it needs a preflight the gateway never approves.
 */
export const REQUESTED_WITH_FETCH = 'fetch';

/** Paging limits shared by every list endpoint. */
/** Digits of an authenticator app's code (two-step sign-in): what every common app shows. */
export const TOTP_DIGITS = 6;
/** Seconds one code of an authenticator app stands for. */
export const TOTP_PERIOD_SECONDS = 30;

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

/**
 * Default of the gateway's own per-upload cap (`MAX_UPLOAD_BYTES`, bytes). The
 * account's real limits come from the mail server (`account.limits`); clients
 * fall back to this only while those are unknown.
 */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/** Undo-send window bounds (seconds) of the app's own, client-side delay: it holds the message itself. */
export const UNDO_SEND_MIN_SECONDS = 0;
export const UNDO_SEND_MAX_SECONDS = 30;
export const UNDO_SEND_DEFAULT_SECONDS = 5;

/**
 * Outer bound of the *server-side* undo send (`delaySeconds` on
 * `POST /mail/send`), where the gateway holds the message and a client that
 * closed its window can still take it back. A deployment narrows it with
 * `MAIL_UNDO_SEND_MAX_SECONDS` (120 s by default): beyond a couple of minutes
 * "sent" stops meaning sent, and scheduling belongs on the mail server itself.
 */
export const PENDING_SEND_MAX_DELAY_SECONDS = 600;

/**
 * Bounds of a *scheduled* send (`scheduledSendAt` on a draft, GAP D7), which
 * the mail server owns rather than the gateway. Far enough ahead that the user
 * meant it, near enough that the draft is still recognisably theirs.
 */
export const SCHEDULED_SEND_MIN_SECONDS = 60;
export const SCHEDULED_SEND_MAX_DAYS = 365;

/**
 * Well-known folder roles; a folder someone made has none.
 *
 * The list and the rules that read it live here rather than beside the schema
 * so a browser can have them without the contracts: importing a value out of
 * the schema half pulls zod into the first download (ADR 0006).
 */
export const SYSTEM_FOLDER_KINDS = [
  'inbox',
  'sent',
  'drafts',
  'trash',
  'junk',
  'archive',
  'outbox',
] as const;
export type SystemFolderKind = (typeof SYSTEM_FOLDER_KINDS)[number];

/**
 * Whether emptying a folder takes the folders inside it with it.
 *
 * On the mail server `empty` is recursive, and for the Trash that is exactly
 * what the word means: a folder someone deleted is *in* the Trash, so emptying
 * it has to remove that too, or the Trash is never empty. Anywhere else the
 * subfolders are part of the mailbox a person built, and emptying a folder of
 * mail must not quietly delete them (GAP B2). Both adapters and the webmail's
 * confirmation read the rule from here, so none of them can drift apart.
 */
export function emptyRemovesSubfolders(systemKind: SystemFolderKind | null): boolean {
  return systemKind === 'trash' || systemKind === 'junk';
}

/** Well-known Zimbra folder ids. They are stable across every Zimbra installation. */
export const SYSTEM_FOLDER_IDS = {
  root: '1',
  inbox: '2',
  trash: '3',
  junk: '4',
  sent: '5',
  drafts: '6',
  contacts: '7',
  calendar: '10',
  emailedContacts: '13',
  chats: '14',
  tasks: '15',
  briefcase: '16',
} as const;

/**
 * Zimbra target constraint (`tcon`) for a conversation action performed while
 * the conversation is listed in `folderId`: t=Trash, j=Junk, s=Sent, d=Drafts;
 * a leading `-` excludes them, so the Inbox and user folders never reach them.
 */
export function conversationScopeConstraint(folderId: string): string {
  switch (folderId) {
    case SYSTEM_FOLDER_IDS.trash:
      return 't';
    case SYSTEM_FOLDER_IDS.junk:
      return 'j';
    case SYSTEM_FOLDER_IDS.sent:
      return 's';
    case SYSTEM_FOLDER_IDS.drafts:
      return 'd';
    default:
      return '-tjsd';
  }
}

/** Deep link scheme registered by the mobile app. */
export const DEEP_LINK_SCHEME = 'postamerkezi';

/** Notification category identifier (iOS category / Android actions group). */
export const PUSH_CATEGORY_MAIL = 'MAIL_MESSAGE';
export const PUSH_CATEGORY_CALENDAR = 'CALENDAR_EVENT';
export const PUSH_CATEGORY_TASK = 'TASK_ASSIGNED';

/** Android notification channels. */
export const PUSH_CHANNELS = {
  mail: 'mail',
  calendar: 'calendar',
  tasks: 'tasks',
  silent: 'silent',
  /** Announcements sent by an administrator from the panel. */
  system: 'system',
  /** Maintenance / service notices sent from the panel. */
  maintenance: 'maintenance',
  /** Newsletters (low importance). */
  newsletters: 'newsletters',
} as const;

/** Supported UI locales, first one is the default. */
export const SUPPORTED_LOCALES = ['tr', 'en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'tr';

/**
 * Real-user measurements (GAP H1). The names, verdicts and route patterns live
 * here rather than beside their schemas so a browser can read them from
 * `@sunucupark/shared/lite` — the reporter must not drag zod into the page it
 * is measuring.
 */
export const WEB_VITAL_NAMES = ['LCP', 'INP', 'CLS', 'TTFB', 'FCP'] as const;
export type WebVitalName = (typeof WEB_VITAL_NAMES)[number];

export const WEB_VITAL_RATINGS = ['good', 'needs-improvement', 'poor'] as const;
export type WebVitalRating = (typeof WEB_VITAL_RATINGS)[number];

/**
 * Route patterns a report may name. An open list would let anyone inflate the
 * label cardinality of the metrics; anything else is reported as `other`.
 */
export const TELEMETRY_ROUTES = ['/sign-in', '/mail/$folderId', '/settings', '/compose/$draftId', 'other'] as const;
export type TelemetryRoute = (typeof TELEMETRY_ROUTES)[number];

export const TELEMETRY_CLIENTS = ['web', 'desktop'] as const;
export type TelemetryClient = (typeof TELEMETRY_CLIENTS)[number];

/** Above this a value is a bug or a lie, not a measurement (ms, or CLS units). */
export const MAX_VITAL_VALUE = 600_000;
/** At most this many measurements ride in one report. */
export const MAX_VITALS_PER_REPORT = 8;
